import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";

export interface SearchableSelectProps<T> {
  items: readonly T[];
  /** Currently selected key, or "" for nothing selected. */
  value: string;
  onChange: (key: string) => void;
  getKey: (item: T) => string;
  /** Primary line. What this returns is BOTH what is shown and what is searched. */
  getLabel: (item: T) => string;
  /** Optional dimmer second line (e.g. "domain · seller"), also searched. */
  getHint?: (item: T) => string | null;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  /** Marks the trigger for tests; rows get `${testId}-option`. */
  testId?: string;
}

/**
 * Dropdown for lists too long to scan in a native <select> — hundreds of
 * sellers or stores — with a type-to-filter box and full keyboard control.
 *
 * Filtering runs over getLabel/getHint, i.e. the text actually on screen, never
 * over the caller's raw record. That is deliberate: these pickers render PII
 * through tutorial-mode masking, and matching a masked row against the unmasked
 * email behind it would hand back the identity the mask just removed.
 */
export function SearchableSelect<T>({
  items,
  value,
  onChange,
  getKey,
  getLabel,
  getHint,
  label,
  placeholder,
  searchPlaceholder,
  loading = false,
  disabled = false,
  id,
  testId,
}: SearchableSelectProps<T>) {
  const t = useT();
  const autoId = useId();
  const fieldId = id ?? autoId;
  const listId = `${fieldId}-list`;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const rows = useMemo(
    () =>
      items.map((item) => {
        const text = getLabel(item);
        const hint = getHint?.(item) ?? null;
        return {
          key: getKey(item),
          text,
          hint,
          search: `${text} ${hint ?? ""}`.toLowerCase(),
        };
      }),
    [items, getKey, getLabel, getHint],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.search.includes(term));
  }, [rows, q]);

  const selected = rows.find((r) => r.key === value) ?? null;

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Typing shrinks the list, so an index left over from the previous query can
  // point past the end — the highlight would vanish and Enter would pick nothing.
  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function commit(key: string) {
    onChange(key);
    setOpen(false);
    setQ("");
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + filtered.length) % filtered.length);
      return;
    }
    if (e.key === "Enter" && open) {
      e.preventDefault();
      const row = filtered[active];
      if (row) commit(row.key);
    }
  }

  const triggerText = loading
    ? t("common.loading")
    : (selected?.text ?? placeholder ?? t("common.select.placeholder"));

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={fieldId} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}
      <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
        <button
          id={fieldId}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listId : undefined}
          disabled={disabled || loading}
          data-testid={testId}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm",
            "focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100",
            "disabled:cursor-not-allowed disabled:bg-slate-50",
            !disabled && !loading && "hover:border-slate-400",
          )}
        >
          <span className={cn("truncate", selected ? "text-slate-900" : "text-slate-400")}>
            {triggerText}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            <div className="border-b border-slate-100 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={searchPlaceholder ?? t("common.select.search")}
                  aria-label={searchPlaceholder ?? t("common.select.search")}
                  aria-controls={listId}
                  className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-300"
                />
              </div>
            </div>
            <ul id={listId} ref={listRef} role="listbox" className="max-h-60 overflow-auto p-1">
              {filtered.length === 0 ? (
                <li className="px-2 py-3 text-center text-xs text-slate-400">
                  {t("common.select.empty", { q: q.trim() })}
                </li>
              ) : (
                filtered.map((row, i) => (
                  <li key={row.key} role="option" aria-selected={row.key === value}>
                    <button
                      type="button"
                      data-testid={testId ? `${testId}-option` : undefined}
                      data-active={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => commit(row.key)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                        row.key === value ? "bg-brand-50 text-brand-800" : "text-slate-700",
                        i === active && row.key !== value && "bg-slate-50",
                      )}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{row.text}</span>
                        {row.hint && (
                          <span className="truncate text-[11px] text-slate-400">{row.hint}</span>
                        )}
                      </span>
                      {row.key === value && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
